import { useEffect, useState } from 'react';
import { Pie, PieChart, Sector } from 'recharts';
import EventsService from '../../services/EventsService';
import { useUserStore } from '../../contexts/userStore';
import { useSSEContext } from '../../contexts/sse';
import { changeAlpha, generatePastelRGBColor, randomPastelColor } from '../../utils/color';
import { FinishBatchAlarms } from '../../components/FinishBatchAlarms/FinishBatchAlarms';
import { DeleteBatchAlarms } from '../../components/DeleteBatchAlarms/DeleteBatchAlarms';
import { useTranslation } from 'react-i18next';
import { getEventData } from '../../constants/events';

const availableColors = [
   '#0088FE',
   '#00C49F',
   '#FFBB28',
   '#FF8042',
   '#FF8082',
   '#FF8084',
   '#FF8086',
   '#FF8088',
   '#FF808A',
   '#FF808C',
];

const renderActiveShape = ({
   cx,
   cy,
   midAngle,
   innerRadius,
   outerRadius,
   startAngle,
   endAngle,
   cornerRadius,
   fill,
   payload,
   percent,
   value,
}) => {
   const RADIAN = Math.PI / 180;
   const sin = Math.sin(-RADIAN * (midAngle ?? 1));
   const cos = Math.cos(-RADIAN * (midAngle ?? 1));
   const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
   const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
   const mx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
   const my = (cy ?? 0) + ((outerRadius ?? 0) + 22) * sin;
   const ex = mx + (cos >= 0 ? 1 : -1) * 10;
   const ey = my;
   const textAnchor = cos >= 0 ? 'start' : 'end';

   const percentFinished = (payload.finished / payload.total);

   return (
      <g>
         <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius * 1.15}
            outerRadius={outerRadius}
            cornerRadius={cornerRadius / 2}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
         />
         <Sector
            cx={cx}
            cy={cy}
            startAngle={startAngle}
            endAngle={endAngle}
            innerRadius={(outerRadius ?? 0) + 6}
            outerRadius={(outerRadius ?? 0) + 10}
            cornerRadius={cornerRadius}
            fill={changeAlpha(0.4, fill)}
         />
         <Sector
            cx={cx}
            cy={cy}
            startAngle={(startAngle + (endAngle - startAngle) * (1 - percentFinished))}
            endAngle={endAngle}
            innerRadius={(outerRadius ?? 0) + 6}
            outerRadius={(outerRadius ?? 0) + 10}
            cornerRadius={1}
            fill={fill}
         />
         {/* <path
            d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
            stroke={fill}
            fill="none"
         />
         <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" /> */}
         <text
            x={ex + (cos >= 0 ? 1 : -1) * 1}
            y={ey}
            textAnchor={textAnchor}
            fill="var(--textColor)"
            fontSize={11}
         >{`${payload.finished}/${payload.total}`}</text>
         <text
            x={ex + (cos >= 0 ? 1 : -1) * 1}
            y={ey}
            dy={12}
            textAnchor={textAnchor}
            fill="#999"
            fontSize={9}
         >
            {`(${((percent ?? 1) * 100).toFixed(0)}%)`}
         </text>
      </g>
   );
};

export default function AlarmsByCategories({
   isAnimationActive = true,
}) {
   const { selectedCondominio } = useUserStore();
   const { sseReceived } = useSSEContext();
   const [alarms, setAlarms] = useState({
      data: [],
      finished: 0,
      pending: 0,
   });
   const { t } = useTranslation();

   const getCountAlarms = async () => {
      const response = await EventsService.getCountAlarms(selectedCondominio);
      let currentIdx = 0;
      setAlarms({
         data: [
            ...[...response?.data?.countAlarms, ...response?.data?.countAutomationAlarms].map(alarm => {
               return {
                  name: alarm.name,
                  pending: alarm.pending ?? 0,
                  finished: alarm.finished ?? 0,
                  total: (alarm.finished ?? 0) + (alarm.pending ?? 0),
                  fill: availableColors?.[currentIdx++] ?? randomPastelColor(20, 40),
               };
            }),
         ],
         finished: response?.data?.finished,
         pending: response?.data?.pending,
      });
   };

   useEffect(() => {
      getCountAlarms();
   }, [selectedCondominio]);

   useEffect(() => {
      if (["deviceBatchEventsConfirmation", "deviceBatchEventsDelete"].includes(sseReceived?.action)) {
         getCountAlarms();
         return;
      }
      if (["deviceEventConfirmation", "createEvent"].includes(sseReceived?.action)) {
         if (sseReceived?.content?.type === "Alarme" && sseReceived?.content?.alarm_confirmation === null) {
            setAlarms(prevAlarms => {
               const newAlarms = [...prevAlarms.data];
               const alarmIndex = newAlarms.findIndex(alarm => alarm.name === sseReceived?.content?.name);
               if (alarmIndex !== -1) {
                  newAlarms[alarmIndex] = {
                     ...newAlarms[alarmIndex],
                     pending: newAlarms[alarmIndex].pending + 1,
                     total: newAlarms[alarmIndex].total + 1,
                  };
               } else {
                  newAlarms.push({
                     name: sseReceived?.content?.name,
                     pending: 1,
                     finished: 0,
                     total: 1,
                     fill: generatePastelRGBColor(20, 40),
                  });
               }
               return {
                  ...prevAlarms,
                  data: newAlarms,
                  pending: prevAlarms.pending + 1,
               };
            });
         }
         if (sseReceived?.content?.type === "Alarme" && sseReceived?.content?.alarm_confirmation !== null) {
            setAlarms(prevAlarms => {
               const newAlarms = [...prevAlarms.data];
               const alarmIndex = newAlarms.findIndex(alarm => alarm.name === sseReceived?.content?.name);
               if (alarmIndex !== -1) {
                  newAlarms[alarmIndex] = {
                     ...newAlarms[alarmIndex],
                     pending: newAlarms[alarmIndex].pending - 1,
                     finished: newAlarms[alarmIndex].finished + 1,
                  };
               }
               return {
                  ...prevAlarms,
                  data: newAlarms,
                  pending: prevAlarms.pending - 1,
                  finished: prevAlarms.finished + 1,
               };
            });
         }
      }
   }, [sseReceived]);

   return (
      <div className='w-[310px] flex flex-col gap-2.5 sticky -top-1 h-fit'>
         <div className='w-full relative shadow-(--box-shadow) border! border-(--borderColor)! rounded-lg px-2! pt-2! pb-10! box-border!'>
            <div className='text-center! text-(--textColor) absolute top-[124px]! left-1/2! -translate-1/2!'>
               <h4 className='font-bold! text-xl! inline-flex gap-1 items-center!'>
                  <span>{alarms.finished}</span>
                  <span className='text-sm! leading-3 opacity-75'>/</span>
                  <span className='text-sm! leading-3 opacity-75'>{alarms.pending + alarms.finished}</span>
               </h4>
               <p className='text-sm opacity-60'>{t("common.resolved")}</p>
            </div>
            <PieChart
               style={{
                  width: '100%',
                  height: '260px'
               }}
               responsive
               margin={{
                  top: 10,
                  right: 60,
                  bottom: 30,
                  left: 60,
               }}
            >
               <Pie
                  activeShape={(args) => renderActiveShape({ ...args })}
                  data={alarms.data}
                  innerRadius="76%"
                  outerRadius="100%"
                  cornerRadius={2}
                  fill="#8884d8"
                  paddingAngle={2}
                  dataKey="total"
                  stroke=''
                  isAnimationActive={isAnimationActive}
               />
            </PieChart>
            <div className='flex w-full justify-center -mt-3!'>
               <div className='inline-flex flex-col gap-1'>
                  {alarms.data.map(category => {
                     const event = getEventData(category.name);
                     return (
                        <div key={category.name} className='inline-flex flex-row items-center gap-2'>
                           <span className='size-4 rounded-lg bg-(--categoryColor)' style={{ '--categoryColor': category.fill }}></span>
                           <span className='text-(--textColor)'>{t(event?.name) || category.name}</span>
                        </div>
                     );
                  })}
               </div>
            </div>
         </div>
         <div
            className="reception-card-container "
            style={{
               alignItems: "center",
               display: "flex",
               flexDirection: "column",
               height: 'fit-content',
            }}
         >
            <div
               className="event-details-title-container"
               style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "0px",
               }}
            >
               <h1 className="event-details-title">{t("common.batchActions")}</h1>
            </div>
            <div className='grid grid-cols-2 w-full *:w-full! gap-2.5 p-3! box-border!'>
               <FinishBatchAlarms categories={alarms.data} />
               <DeleteBatchAlarms categories={alarms.data} />
            </div>
         </div>
      </div>
   );
}
